import { useContext, useEffect, useState } from "react";
import { Outlet, useParams } from "react-router-dom";

import { Organization } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Box, Flex, rem, Title } from "@mantine/core";

import { Loading } from "@/components/Loading";
import PractitionerContainer from "@/components/PractitionerContainer";
import { BackgroundContext } from "@/contexts/background";
import NotFound from "@/NotFound";

import Sidebar, { SettingsSectionSelect, useSettingsNavLinks } from "../Sidebar";

import classes from "./PracticeSettings.module.css";

export default function PracticeSettings() {
  const { practiceId } = useParams() as { practiceId: string };
  const { setBackground } = useContext(BackgroundContext);
  const [isLoading, setIsLoading] = useState(true);
  const [organization, setOrganization] = useState<Organization>();
  const medplum = useMedplum();
  // One set of links for both navigations: the sidebar from sm up, the select on phones.
  const settingsLinks = useSettingsNavLinks();

  useEffect(() => {
    setBackground("white");
  }, [setBackground]);

  useEffect(() => {
    medplum
      .readResource("Organization", practiceId)
      .then((resource) => {
        setOrganization(resource);
        setIsLoading(false);
      })
      .catch((error) => {
        if (error.message !== "Not found") throw error;
      })
      .finally(() => setIsLoading(false));
  }, [medplum, practiceId]);

  if (isLoading) {
    return <Loading />;
  }

  if (!organization) {
    return <NotFound />;
  }

  // Main header + page header + border bottom of page header
  const minimumPageHeightCalc = `calc(100vh - ${rem(60 + 80 + 1)})`;
  return (
    <PractitionerContainer>
      <Box px="md" w="100%" bg="white" className={classes.header}>
        <Flex m="0 auto" h={rem(80)} align="center" justify="space-between">
          <Title order={1}>Settings</Title>
        </Flex>
      </Box>
      <Flex mih={minimumPageHeightCalc}>
        <Sidebar links={settingsLinks} />
        {/* miw 0 lets the content shrink below its tables' widths, so they scroll in place. */}
        <Box miw={0} w="100%" m={{ base: "md", sm: "lg" }}>
          <SettingsSectionSelect links={settingsLinks} />
          <Outlet />
        </Box>
      </Flex>
    </PractitionerContainer>
  );
}
